import {
  Box,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, analytics } from "common/analytics";
import EventFormDialog from "common/components/EventFormDialog";
import { guid } from "common/guid";
import { useTranslation } from "common/locales";
import { FieldIcon } from "features/farmOverview";
import { Field, ManualDMInputItem, useFields } from "model";
import { getAllFieldGeomsFromFields } from "model/fieldHelpers";
import { useCreateReadingsBulk } from "model/useManualReadingsQueries";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EventType } from "../../model/manualInputTypes";

const cardSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  p: 2.5,
};

export default function AddCoverDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const { notify } = useNotification();
  const { t } = useTranslation();

  const { mutateAsync: createReadingsBulk } = useCreateReadingsBulk();

  const { data: fields = [], isLoading } = useFields();

  const [manualDMInputs, setManualDMInputs] = useState<ManualDMInputItem[]>([]);
  const [measurementDate, setMeasurementDate] = useState<Date | null>(
    new Date()
  );

  const fieldGeoms = useMemo(
    () => getAllFieldGeomsFromFields(fields),
    [fields]
  );

  // Initialize manualDMInputs when fields load
  useEffect(() => {
    if (fields.length > 0 && manualDMInputs.length === 0) {
      setManualDMInputs(
        fields.map((field: Field) => ({
          dryMatterValue: field.dryMatterNow,
          field: field.guid,
          fieldName: field.name,
          event: { eventType: EventType.None },
          kind: "user",
          verificationStatus: "Accepted",
        }))
      );
    }
  }, [fields]);

  const hasChanges = useCallback(() => {
    return manualDMInputs.some(
      (input) => input.newDryMatterValue !== undefined
    );
  }, [manualDMInputs]);

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleDateChange = (d: Date | null) => {
    setMeasurementDate(d);
  };

  const handleDMInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldId: string
  ) => {
    setManualDMInputs(
      manualDMInputs.map((i) => {
        if (i.field === fieldId) {
          return {
            ...i,
            newDryMatterValue: e.target.value
              ? Number(e.target.value)
              : undefined,
          };
        }
        return i;
      })
    );
  };

  async function handleSave() {
    const preparedInputs = manualDMInputs.reduce(
      (acc: ManualDMInputItem[], manualInput) => {
        if (manualInput.newDryMatterValue) {
          acc.push({
            ...manualInput,
            dryMatterValue: manualInput.newDryMatterValue,
          });
        }
        return acc;
      },
      []
    );

    if (preparedInputs.length === 0 || !measurementDate) return;

    const startDate = new Date(measurementDate);
    startDate.setHours(12, 0, 0, 0);
    const endDate = new Date(startDate.getTime() + 5 * 60000);

    await createReadingsBulk(
      preparedInputs.map((i) => ({
        guid: guid(),
        manualDryMatterInput: {
          ...i,
          dateTimeMeasurementStart: startDate.toISOString(),
          dateTimeMeasurementEnd: endDate.toISOString(),
          event: {
            ...i.event,
            dateTimeCut:
              i.event.dateTimeCut && i.event.dateTimeCut.toISOString(),
            dateTimeIn: i.event.dateTimeIn && i.event.dateTimeIn.toISOString(),
            dateTimeOut:
              i.event.dateTimeOut && i.event.dateTimeOut.toISOString(),
          },
        },
      }))
    );

    notify(t("addCovers.success"));
    analytics.track(AnalyticsEvents.BULK_COVER_CREATED);
    handleClose();
  }

  const steps = [
    {
      content: isLoading ? null : (
        <Container style={{ textAlign: "left" }}>
          <Grid container spacing={2.5}>
            {/* Date & time */}
            <Grid size={12}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                {t("addDate.enterTime.dateLabel")}
              </Typography>
              <Box sx={cardSx}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DatePicker
                        disableFuture
                        format="MMMM do yyyy"
                        label={t("addDate.enterTime.dateLabel")}
                        value={measurementDate}
                        slotProps={{
                          textField: { fullWidth: true },
                        }}
                        onChange={handleDateChange}
                      />
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </Box>
            </Grid>

            {/* Cover values */}
            <Grid size={12}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                {t("addCovers.enterCovers.heading")}
              </Typography>
              <Box sx={cardSx}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ display: { xs: "none", sm: "table-cell" } }}
                        />
                        <TableCell>
                          {t("addCovers.enterCovers.fieldName")}
                        </TableCell>
                        <TableCell align="center">
                          {t("addCovers.enterCovers.current")}
                        </TableCell>
                        <TableCell align="center">
                          {t("addCovers.enterCovers.new")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {manualDMInputs.map((reading) => (
                        <TableRow key={reading.field}>
                          <TableCell
                            sx={{
                              p: 1,
                              display: { xs: "none", sm: "table-cell" },
                            }}
                          >
                            <FieldIcon
                              geom={fieldGeoms.features.find(
                                (f) => f.properties?.id === reading.field
                              )}
                              dryMatterNow={reading.dryMatterValue}
                            />
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {reading.fieldName}
                          </TableCell>
                          <TableCell align="center">
                            {reading.dryMatterValue}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              value={reading.newDryMatterValue ?? ""}
                              type="number"
                              onChange={(e) => handleDMInput(e, reading.field)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
        </Container>
      ),
    },
  ];

  return (
    <EventFormDialog
      open={dialogOpen}
      onClose={handleClose}
      title={t("addCovers.dialog.title")}
      onSave={handleSave}
      saveDisabled={!hasChanges() || !measurementDate}
      isDirty={hasChanges}
      cancelAnalyticsEvent={AnalyticsEvents.BULK_COVER_INPUT_CANCELLED}
      steps={steps}
    />
  );
}
